import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { UsersThree, UserPlus, Trash, PencilSimple, Crown, WarningCircle, ShieldCheck, FloppyDisk, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", role: "viewer" };

export default function Teams() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState("");
  const [permissionData, setPermissionData] = useState(null);
  const [permissionRole, setPermissionRole] = useState("warehouse");
  const [permissionBusy, setPermissionBusy] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);

  const load = async () => {
    const [team, permissions] = await Promise.all([api.get("/team"), api.get("/team/role-permissions")]);
    setData(team.data);
    setPermissionData(permissions.data);
  };

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);

  const roles = useMemo(() => data?.roles || {}, [data]);
  const canInvite = data && (data.user_limit == null || data.used < data.user_limit) && ["pro", "business", "enterprise"].includes(data.package);
  const selectedMenus = permissionData?.permissions?.[permissionRole] || [];

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/team/invitations", form);
      toast.success("Ekip daveti e-posta ile gönderildi");
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      await load().catch(() => {});
    } finally { setBusy(false); }
  };

  const resend = async (m) => {
    setResending(m.id);
    try {
      await api.post(`/team/invitations/${m.id}/resend`);
      toast.success("Davet e-postası tekrar gönderildi");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResending(""); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.put(`/team/members/${editing.id}`, { name: editing.name || "", role: editing.role });
      toast.success("Ekip üyesi güncellendi");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`${m.email} ekipten kaldırılsın mı?`)) return;
    try {
      await api.delete(`/team/members/${m.id}`);
      toast.success("Ekip üyesi kaldırıldı");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleMenu = (menuKey) => {
    setPermissionData((prev) => {
      if (!prev) return prev;
      const current = prev.permissions?.[permissionRole] || [];
      const next = current.includes(menuKey) ? current.filter((x) => x !== menuKey) : [...current, menuKey];
      return { ...prev, permissions: { ...prev.permissions, [permissionRole]: next } };
    });
  };

  const saveRolePermissions = async () => {
    setPermissionBusy(true);
    try {
      await api.put(`/team/role-permissions/${permissionRole}`, { menus: selectedMenus });
      toast.success(`${permissionData?.roles?.[permissionRole] || permissionRole} menü yetkileri kaydedildi`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPermissionBusy(false); }
  };

  return (
    <div data-testid="teams-page">
      <PageHeader title="Ekip & Yetkiler" subtitle="Kullanıcı koltuklarını, davetleri, rolleri ve rol bazlı menü erişimlerini yönetin" />
      <div className="p-4 sm:p-8 max-w-6xl space-y-6">
        <section className="grid md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 p-5"><div className="text-[10px] uppercase tracking-widest text-slate-500">Paket</div><div className="mt-2 flex items-center gap-2 text-xl font-bold"><Crown size={22} weight="duotone" /> {data?.package_label || "—"}</div></div>
          <div className="bg-white border border-slate-200 p-5"><div className="text-[10px] uppercase tracking-widest text-slate-500">Kullanıcı</div><div className="mt-2 text-2xl font-extrabold">{data?.used ?? "—"} / {data?.user_limit == null ? "∞" : data.user_limit}</div></div>
          <div className="bg-white border border-slate-200 p-5"><div className="text-[10px] uppercase tracking-widest text-slate-500">Boş Koltuk</div><div className="mt-2 text-2xl font-extrabold">{data?.remaining == null ? "∞" : data.remaining}</div></div>
        </section>

        {data && !["pro", "business", "enterprise"].includes(data.package) && <div className="bg-amber-50 border border-amber-200 p-4 flex gap-3 text-sm text-amber-900"><WarningCircle size={22} className="shrink-0" /><div><b>Ekip özelliği PRO ve üzeri paketlerde kullanılabilir.</b><br/>PRO: 5 kullanıcı · Business: 25 kullanıcı.</div></div>}

        <section className="bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4"><UserPlus size={20} weight="duotone"/><h2 className="font-bold">Üye Davet Et</h2></div>
          <form onSubmit={invite} className="grid md:grid-cols-[1fr_1.3fr_1fr_auto] gap-3 items-end">
            <label className="block"><span className="text-xs font-semibold text-slate-600">Ad Soyad</span><input className="input mt-1" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Örn. Ayşe Yılmaz" /></label>
            <label className="block"><span className="text-xs font-semibold text-slate-600">E-mail</span><input className="input mt-1" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} placeholder="kullanici@firma.com" /></label>
            <label className="block"><span className="text-xs font-semibold text-slate-600">Rol</span><select className="input mt-1" value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})}>{Object.entries(roles).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></label>
            <button disabled={!canInvite || busy} className="bg-[#2563EB] text-white px-5 py-2.5 font-semibold disabled:opacity-40">{busy ? "Gönderiliyor…" : "Davet Et"}</button>
          </form>
        </section>

        <section className="bg-white border border-slate-200" data-testid="role-menu-permissions">
          <button type="button" onClick={()=>setPermissionsOpen(v=>!v)} className="w-full p-5 flex items-center justify-between gap-3 text-left">
            <div><div className="flex items-center gap-2"><ShieldCheck size={21} weight="duotone"/><h2 className="font-bold">Rol Bazlı Menü Yetkileri</h2></div><p className="text-sm text-slate-500 mt-1">Her rolün hangi PalletNext menülerini görebileceğini hesap sahibi belirler.</p></div>
            <CaretDown size={20} className={`shrink-0 transition-transform ${permissionsOpen?"rotate-180":""}`}/>
          </button>
          {permissionsOpen&&<div className="border-t border-slate-200 p-5">
            <div className="flex justify-end mb-4"><select className="input lg:w-64" value={permissionRole} onChange={(e)=>setPermissionRole(e.target.value)}>{Object.entries(permissionData?.roles || {}).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(permissionData?.menus || {}).map(([key,label])=><label key={key} className={`flex items-center gap-3 border p-3 cursor-pointer ${selectedMenus.includes(key)?"border-blue-300 bg-blue-50":"border-slate-200 bg-white"}`}><input type="checkbox" checked={selectedMenus.includes(key)} onChange={()=>toggleMenu(key)} /><span className="text-sm font-medium">{label}</span></label>)}
            </div>
            <div className="mt-4 flex justify-end"><button onClick={saveRolePermissions} disabled={permissionBusy} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 font-semibold disabled:opacity-50"><FloppyDisk size={16}/>{permissionBusy?"Kaydediliyor…":"Yetkileri Kaydet"}</button></div>
          </div>}
        </section>

        <section className="bg-white border border-slate-200 overflow-x-auto">
          <div className="p-4 border-b flex items-center gap-2 font-bold"><UsersThree size={20} weight="duotone"/>Ekip Listesi</div>
          <table className="data-table w-full min-w-[900px]">
            <thead><tr><th>Kullanıcı</th><th>E-mail</th><th>Rol</th><th>Durum</th><th>E-mail</th><th className="text-right">İşlem</th></tr></thead>
            <tbody>
              {(data?.members || []).map((m)=><tr key={m.id}>
                <td className="font-semibold">{m.name || "—"}{m.is_owner && <span className="ml-2 text-[10px] bg-slate-900 text-white px-2 py-0.5">OWNER</span>}</td>
                <td>{m.email}</td><td>{m.role_label || m.role}</td>
                <td><span className={`text-xs px-2 py-1 border ${m.status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{m.status === "pending" ? "Davet Bekliyor" : "Aktif"}</span></td>
                <td>{m.is_owner ? "—" : m.email_error ? <span className="text-xs text-red-600" title={m.email_error}>Gönderilemedi</span> : m.email_sent_at ? <span className="text-xs text-emerald-700">Gönderildi</span> : <span className="text-xs text-slate-400">Bekliyor</span>}</td>
                <td><div className="flex justify-end gap-2">{!m.is_owner && <>{m.status === "pending" && <button onClick={()=>resend(m)} disabled={resending===m.id} className="px-3 py-2 border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50 text-xs font-semibold">{resending===m.id?"Gönderiliyor…":"Tekrar Gönder"}</button>}<button onClick={()=>setEditing({...m})} className="p-2 border border-slate-200 hover:bg-slate-50" title="Düzenle"><PencilSimple size={16}/></button><button onClick={()=>remove(m)} className="p-2 border border-red-200 text-red-600 hover:bg-red-50" title="Kaldır"><Trash size={16}/></button></>}</div></td>
              </tr>)}
              {!data?.members?.length && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Ekip verisi yükleniyor…</td></tr>}
            </tbody>
          </table>
        </section>
      </div>

      {editing && <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onMouseDown={()=>setEditing(null)}><div className="bg-white w-full max-w-lg p-6 shadow-2xl" onMouseDown={(e)=>e.stopPropagation()}><h3 className="text-xl font-bold">Ekip Üyesini Düzenle</h3><div className="mt-4 space-y-4"><label className="block"><span className="text-xs font-semibold">Ad Soyad</span><input className="input mt-1" value={editing.name || ""} onChange={(e)=>setEditing({...editing,name:e.target.value})}/></label><label className="block"><span className="text-xs font-semibold">Rol</span><select className="input mt-1" value={editing.role} onChange={(e)=>setEditing({...editing,role:e.target.value})}>{Object.entries(roles).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></label></div><div className="mt-6 flex justify-end gap-2"><button onClick={()=>setEditing(null)} className="px-4 py-2 border">Vazgeç</button><button onClick={saveEdit} disabled={busy} className="px-4 py-2 bg-[#2563EB] text-white font-semibold disabled:opacity-50">Kaydet</button></div></div></div>}
    </div>
  );
}
